import { useEffect, useState } from "react";
import { Calendar, Copy, RefreshCw, Eye, EyeOff, Loader2, ExternalLink } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Externer Kalender-Abo-Block (.ics).
 *
 * Zeigt eine persönliche Abo-URL, mit der externe Kalender (Outlook, Google,
 * Apple) die Novatus-Termine live abonnieren können. Token rotierbar.
 *
 * Deep-link: /profile?ics=1 → scrollt automatisch zu dieser Section und hebt
 * sie kurz hervor (Highlight-Animation).
 */
export default function CalendarIcsSubscription() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [info, setInfo] = useState(null); // { url, webcal_url, token, created_at }
  const [showToken, setShowToken] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const [showNewBadge, setShowNewBadge] = useState(() => !localStorage.getItem("novatus_ics_section_opened"));

  // Mark the section as "seen" — removes the NEU badge.
  const markSeen = () => {
    if (showNewBadge) {
      localStorage.setItem("novatus_ics_section_opened", "1");
      setShowNewBadge(false);
    }
  };

  // Deep-link Handler: ?ics=1 → smooth scroll + flash highlight + auto-mark-seen
  useEffect(() => {
    if (searchParams.get("ics") === "1") {
      setHighlight(true);
      markSeen();
      const el = document.querySelector('[data-testid="profile-ics-subscription"]');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
      // Remove the query param so reloads don't re-trigger
      const next = new URLSearchParams(searchParams);
      next.delete("ics");
      setSearchParams(next, { replace: true });
      // Fade out highlight after 2.5 s
      const t = setTimeout(() => setHighlight(false), 2500);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/calendar/ics-subscription");
        if (!cancelled) setInfo(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Konnte Abo-Link nicht laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copyUrl = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("URL in Zwischenablage kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen — bitte manuell markieren");
    }
  };

  const rotate = async () => {
    if (!window.confirm(
      "Wirklich Token zurücksetzen?\n\nDer alte Abo-Link wird sofort ungültig — externe Kalender, die ihn nutzen, zeigen keine Termine mehr, bis du den neuen Link einträgst."
    )) return;
    setRotating(true);
    try {
      const r = await api.post("/calendar/ics-subscription/rotate");
      setInfo(r.data);
      toast.success("Neues Token generiert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Token-Rotation fehlgeschlagen");
    } finally {
      setRotating(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-3">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-zinc-800">
          <Calendar className="text-[#00E575]" size={18} />
          <h2 className="font-display text-lg font-semibold">Externer Kalender (Outlook · Google · Apple)</h2>
        </div>
        <div className="flex items-center gap-2 text-zinc-400 text-sm py-3">
          <Loader2 className="animate-spin" size={14} /> lädt…
        </div>
      </div>
    );
  }

  // Mask the token in the URL for display when hidden
  const displayUrl = (() => {
    if (!info) return "";
    if (showToken) return info.url;
    return info.url.replace(/\/ics\/[^.]+\.ics/, "/ics/••••••••••••.ics");
  })();

  return (
    <div
      className={`bg-[#18181B] border rounded-2xl p-5 mb-3 transition-all duration-500 ${highlight ? "border-[#00E575] ring-4 ring-[#00E575]/30 shadow-lg shadow-[#00E575]/20" : "border-zinc-800"}`}
      data-testid="profile-ics-subscription"
      onClick={markSeen}
    >
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-zinc-800">
        <Calendar className="text-[#00E575]" size={18} />
        <h2 className="font-display text-lg font-semibold">Externer Kalender (Outlook · Google · Apple)</h2>
        {showNewBadge && (
          <span
            data-testid="profile-ics-new-badge"
            className="ml-1 inline-flex items-center px-2 py-0.5 rounded-md bg-[#00E575] text-[#09090B] text-[12px] font-bold uppercase tracking-widest animate-pulse"
          >
            Neu
          </span>
        )}
      </div>

      <p className="text-[13px] text-zinc-300 leading-relaxed mb-3">
        Deine Novatus-Termine & fälligen Aufgaben kannst du <b>direkt in Outlook, Google
        oder Apple Kalender abonnieren</b>. Der externe Kalender holt sich die Daten
        automatisch alle 1–4 Stunden — keine doppelte Pflege mehr.
      </p>

      <div className="bg-[#09090B] border border-zinc-700 rounded-xl p-3 mb-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">Persönliche Abo-URL</div>
          <button
            type="button"
            onClick={() => setShowToken((s) => !s)}
            data-testid="profile-ics-toggle-visibility"
            className="text-[12px] text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"
          >
            {showToken ? <><EyeOff size={11} /> ausblenden</> : <><Eye size={11} /> anzeigen</>}
          </button>
        </div>
        <div className="font-mono text-[12px] text-zinc-100 break-all select-all" data-testid="profile-ics-url">
          {displayUrl}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            onClick={() => copyUrl(info.url)}
            data-testid="profile-ics-copy-https"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] text-[12px] font-bold hover:bg-[#00C964] active:scale-[0.97] transition"
          >
            <Copy size={13} /> https-Link kopieren
          </button>
          <button
            type="button"
            onClick={() => copyUrl(info.webcal_url)}
            data-testid="profile-ics-copy-webcal"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-zinc-800 text-zinc-100 text-[12px] font-semibold hover:bg-zinc-700 transition"
          >
            <Copy size={13} /> webcal://-Link kopieren
          </button>
          <button
            type="button"
            onClick={rotate}
            disabled={rotating}
            data-testid="profile-ics-rotate"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/40 text-[12px] font-semibold hover:bg-amber-500/25 transition disabled:opacity-50"
          >
            {rotating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Token zurücksetzen
          </button>
        </div>
      </div>

      {/* Instructions */}
      <details className="bg-[#0F1014] border border-zinc-800 rounded-xl p-3 mb-2" data-testid="profile-ics-howto">
        <summary className="cursor-pointer text-[13px] font-bold text-zinc-100 select-none">
          So abonnierst du den Kalender — Schritt-für-Schritt
        </summary>
        <div className="mt-3 space-y-3 text-[12.5px] text-zinc-300 leading-relaxed">
          <div>
            <div className="font-bold text-[#00E575] mb-1 inline-flex items-center gap-1.5">
              <ExternalLink size={12} /> Outlook (Web / Desktop)
            </div>
            <ol className="list-decimal pl-5 space-y-0.5">
              <li>Outlook → Kalender → <b>„Kalender hinzufügen“</b> → <b>„Aus dem Internet abonnieren“</b>.</li>
              <li><b>https-Link</b> oben einfügen.</li>
              <li>Name vergeben (z. B. „Novatus“) → Speichern. Abruf alle 4–6 h.</li>
            </ol>
          </div>
          <div>
            <div className="font-bold text-[#00E575] mb-1 inline-flex items-center gap-1.5">
              <ExternalLink size={12} /> Google Kalender
            </div>
            <ol className="list-decimal pl-5 space-y-0.5">
              <li>calendar.google.com → links bei „Weitere Kalender“ → <b>„+“</b> → <b>„Per URL“</b>.</li>
              <li><b>https-Link</b> einfügen → „Kalender hinzufügen“. Abruf ca. 8–24 h.</li>
            </ol>
          </div>
          <div>
            <div className="font-bold text-[#00E575] mb-1 inline-flex items-center gap-1.5">
              <ExternalLink size={12} /> Apple Kalender (iPhone / Mac)
            </div>
            <ol className="list-decimal pl-5 space-y-0.5">
              <li>iPhone: Einstellungen → Kalender → Accounts → <b>„Kalenderabo hinzufügen“</b>.</li>
              <li><b>webcal://-Link</b> einfügen → Weiter → Speichern. Abruf konfigurierbar (1 h empfohlen).</li>
            </ol>
          </div>
        </div>
      </details>

      <p className="text-[12px] text-zinc-300 leading-relaxed">
        <b>Datenschutz:</b> Die Abo-URL enthält dein persönliches Token — gib sie nur ein, wo du sie selbst brauchst.
        Wenn du sie versehentlich geteilt hast, klicke <b>„Token zurücksetzen“</b> — alter Link wird ungültig.
        Aufgaben erscheinen als Ganztages-Einträge an ihrem Fälligkeitsdatum. Manager/Admin bekommen nur ihre EIGENEN Termine — keine Team-Termine — im Feed (DSGVO).
      </p>
    </div>
  );
}
